import { useRef, useState, useEffect } from 'react';
import { InputRef, TableColumnsType, TableColumnType, Button, Input, Space, notification } from 'antd';
import { SearchOutlined } from "@ant-design/icons";
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import { fetchCustomerList } from '../utils/customer_master';
import { Resizable, ResizeCallbackData } from 'react-resizable';

const ResizableTitle = (props: any) => {
    const { onResize, width, ...restProps } = props;
    if (!width) return <th {...restProps} />;
    return (
        <Resizable width={width} height={0} handle={<span className="react-resizable-handle" onClick={(e) => e.stopPropagation()} />} onResize={onResize} draggableOpts={{ enableUserSelectHack: false }}>
            <th {...restProps} />
        </Resizable>
    );
};

export interface CustomerData {
    key?: string;
    cs_code: string;
    cs_name: string;
    customer_type: string;
    address_line1: string;
    address_line2: string;
    no_hp1: string;
    record_status: string;
    provinsi: string;
    kabupaten: string;
    kecamatan: string;
    kelurahan: string;
    kode_pos: string;
    no_hp2: string;
    vatnpwp: string;
}

export const useCustomerTable = (onSelect?: (customer: CustomerData) => void) => {
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<CustomerData[]>([]);
    const searchInput = useRef<InputRef>(null);

    useEffect(() => {
        const loadCustomerData = async () => {
            setLoading(true);
            try {
                const customerData = await fetchCustomerList();
                const dataWithKeys = customerData.map((customer: CustomerData) => ({
                    ...customer,
                    key: customer.cs_code
                }));
                setData(dataWithKeys);
                setError(null);
            } catch (err) {
                setError('Failed to fetch customer data');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadCustomerData();
    }, []);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: keyof CustomerData,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: keyof CustomerData): TableColumnType<CustomerData> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ?.toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()) ?? false,
        filterDropdownProps: {
            onOpenChange: (visible) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });
    const [columns, setColumns] = useState<TableColumnsType<CustomerData>>([
        {
            title: 'Customer Code',
            dataIndex: 'cs_code',
            key: 'cs_code',
            width: 150,
            ...getColumnSearchProps('cs_code'),
            sorter: (a, b) => (a.cs_code || '').localeCompare(b.cs_code || ''),
        },
        {
            title: 'Customer Name',
            dataIndex: 'cs_name',
            key: 'cs_name',
            width: 150,
            ...getColumnSearchProps('cs_name'),
            sorter: (a, b) => (a.cs_name || '').localeCompare(b.cs_name || ''),
        },
        {
            title: 'Customer Type',
            dataIndex: 'customer_type',
            key: 'customer_type',
            width: 150,
            ...getColumnSearchProps('customer_type'),
            sorter: (a, b) => (a.customer_type || '').localeCompare(b.customer_type || ''),
        },
        {
            title: 'Address 1',
            dataIndex: 'address_line1',
            key: 'address_line1',
            width: 150,
            ellipsis: true,
            ...getColumnSearchProps('address_line1'),
        },
        {
            title: 'Address 2',
            dataIndex: 'address_line2',
            key: 'address_line2',
            width: 150,
            ellipsis: true,
            ...getColumnSearchProps('address_line2'),
        },
        {
            title: 'Phone Number',
            dataIndex: 'no_hp1',
            key: 'no_hp1',
            width: 150,
            ...getColumnSearchProps('no_hp1'),
        },
        {
            title: 'Status',
            dataIndex: 'record_status',
            key: 'record_status',
            width: 150,
            ...getColumnSearchProps('record_status'),
            sorter: (a, b) => (a.record_status || '').localeCompare(b.record_status || ''),
            render: (status: string) => {
                let color = '';
                let text = '';
                let bgColor = '';

                switch (status) {
                    case '1':
                        color = '#4CAF50';
                        bgColor = '#E8F5E9';
                        text = 'Active';
                        break;
                    case '2':
                        color = '#FF0000';
                        bgColor = '#FFEBEE';
                        text = 'Deactive';
                        break;
                    default:
                        color = '#000000';
                        bgColor = '#F5F5F5';
                        text = status;
                }

                return (
                    <span style={{
                        color: color,
                        backgroundColor: bgColor,
                        padding: '4px 8px',
                        borderRadius: '4px'
                    }}>
                        {text}
                    </span>
                );
            },
            filters: [
                {
                    text: 'Active',
                    value: '1',
                },
                {
                    text: 'Deactive',
                    value: '2',
                },
            ],
            onFilter: (value, record) => record.record_status === value,
        },
    ]);

    const handleResize = (index: number) => (e: React.SyntheticEvent, { size }: ResizeCallbackData) => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            newColumns[index] = { ...newColumns[index], width: size.width };
            return newColumns;
        });
    };

    const handleDoubleClickResize = (index: number) => () => {
        setColumns((prevColumns) => {
            const newColumns = [...prevColumns];
            const currentWidth = newColumns[index].width;
            newColumns[index] = {
                ...newColumns[index],
                width: currentWidth === 150 ? 'fit-content' : 150
            };
            return newColumns;
        });
    };

    const resizableColumns = columns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            onResize: handleResize(index) as any,
            onDoubleClick: handleDoubleClickResize(index),
        }),
    })) as TableColumnsType<CustomerData>;

    const components = { header: { cell: ResizableTitle } };

    return {
        data,
        columns: resizableColumns,
        components,
        loading,
        error,
        searchInput,
        searchText,
        searchedColumn,
    };
};